/**
 * NavLog — the working view of the flight.
 *
 * The Formular is the document; this is the instrument. What a pilot changes
 * here is what the flight IS — the altitude, the speed, the wind, the distance —
 * and everything downstream recomputes through the same store, so the two views
 * can never disagree. There is no second calculation path (§9.1).
 *
 * It fetches nothing. The wind, the terrain and the aerodrome data arrive on
 * their own and land in the store; this pane reads them like any other value and
 * says, per cell, where each came from.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import { EmptyState } from '../../app/EmptyState';
import { Freshness } from '../../components/Freshness';
import { useT } from '../../i18n';
import { useNarrow } from '../../media';
import { usePlanRev, usePlanStore } from '../../planContext';
import { useReadOnly } from '../../components/Field';
import { useWinds } from '../../windsContext';
import { LegCards } from './LegCards';
import { NavLogTable } from './NavLogTable';
import { COLUMNS, hiddenColumns, visibleColumns } from './columns';
import { fuelPerHour, isEmptyRoute, rowsFor } from './model';
import './navlog.css';

/**
 * The pane's own width in px.
 *
 * The inspector is 420 px on a desktop and 340 on an iPad in landscape, so the
 * window is the wrong thing to ask: the same 1440 px screen shows this pane at
 * three widths depending on whether the inspector is collapsed and whether the
 * map is up. `null` until the first measurement, which is what keeps the first
 * frame from rendering twenty columns into a 340 px box.
 */
function useWidth(): { ref: (el: HTMLElement | null) => void; width: number | null } {
  const [width, setWidth] = useState<number | null>(null);
  const obs = useRef<ResizeObserver | null>(null);
  const ref = useMemo(
    () => (el: HTMLElement | null) => {
      obs.current?.disconnect();
      obs.current = null;
      if (!el) return;
      const read = () => {
        const w = el.getBoundingClientRect().width;
        // a pane that is momentarily 0 wide (mounting, a hidden tab) says nothing
        if (w > 0) setWidth(prev => (prev === w ? prev : w));
      };
      read();
      if (typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(read);
      ro.observe(el);
      obs.current = ro;
    },
    [],
  );
  useEffect(() => () => obs.current?.disconnect(), []);
  return { ref, width };
}

/**
 * Put the caret in the route search.
 *
 * The workspace already owns that gesture — it is what `/` does — and it owns it
 * in both places the workspace exists (a saved flight, where the tab is in the
 * URL, and the signed-out local flight, where it is component state). Asking it
 * the way a pilot's keyboard asks keeps one implementation of "find a place".
 */
function focusRouteSearch(): void {
  document.dispatchEvent(new KeyboardEvent('keydown', { key: '/', bubbles: true }));
}

export function NavLogPane() {
  const t = useT();
  const store = usePlanStore();
  const rev = usePlanRev();
  const narrow = useNarrow();
  const readOnly = useReadOnly();
  const winds = useWinds();
  const { ref, width } = useWidth();

  const read = useMemo(() => (id: string) => store.val(id), [store]);
  const empty = useMemo(() => isEmptyRoute(read), [read, rev]);
  const rows = useMemo(() => rowsFor(read, readOnly), [read, rev, readOnly]);
  const lph = useMemo(() => fuelPerHour(read), [read, rev]);
  const stale = winds?.stale ?? new Set<number>();

  const columns = useMemo(
    () => (narrow ? COLUMNS.filter(c => c.key !== 'fuel' || lph !== null) : visibleColumns(width ?? 0, lph !== null)),
    [narrow, width, lph],
  );
  const hidden = useMemo(
    () => (narrow ? [] : hiddenColumns(width ?? 0, lph !== null)),
    [narrow, width, lph],
  );

  return (
    <div
      className="ae-navlog"
      ref={ref}
      /* the expanded row's detail is sized against the pane, not the table */
      style={width === null ? undefined : ({ '--nl-pane-w': width + 'px' } as CSSProperties)}
    >
      <div className="ae-pane-head nl-head">
        <h2 className="ae-eyebrow">{t('navlog.title')}</h2>
        {/* a plan with no legs has no wind to be fresh about */}
        {empty ? null : <Freshness family="winds" />}
      </div>

      {empty ? (
        <EmptyState
          title={t('navlog.empty.title')}
          line={t('navlog.empty.line')}
          action={
            <button type="button" className="ae-btn ae-btn-primary" onClick={focusRouteSearch}>
              {t('navlog.empty.action')}
            </button>
          }
        />
      ) : (
        <>
          <div className="nl-scroll">
            {narrow ? (
              <LegCards rows={rows} columns={columns} lph={lph} stale={stale} read={read} />
            ) : (
              <NavLogTable
                rows={rows}
                columns={columns}
                hidden={hidden}
                lph={lph}
                stale={stale}
                readOnly={readOnly}
                read={read}
                caption={t('navlog.title')}
              />
            )}
          </div>
          {/* the legend invites an edit, so it is not shown to somebody reading */}
          {readOnly ? null : <p className="nl-legend">{t('navlog.legend')}</p>}
        </>
      )}
    </div>
  );
}

export default NavLogPane;
